import React, { useId, useRef } from "react";
import { sanitizeDecimal } from "../../utils/decimalInput";

interface DecimalInputProps {
  id?: string;
  label: string;
  /** Raw text as typed ("84,5"); normalise with normalizeDecimal before storing. */
  value: string;
  onChange: (value: string) => void;
  /** Shown right after the number, and as the placeholder while empty. */
  unit: string;
  message?: string;
  tone?: "error" | "warning";
  maxDecimals?: number;
  disabled?: boolean;
}

const TONE_COLOR = { error: "var(--primary-danger)", warning: "#e17200" };

/**
 * inputMode="decimal", not type="number": iOS shows a comma key and Android cannot type e/+/-.
 * sanitizeDecimal still guards paste and hardware keyboards.
 */
const DecimalInput: React.FC<DecimalInputProps> = ({
  id,
  label,
  value,
  onChange,
  unit,
  message,
  tone = "error",
  maxDecimals = 1,
  disabled,
}) => {
  const generatedId = useId().replace(/:/g, "");
  const inputId = id || `decimal-input-${generatedId}`;
  const messageId = `${inputId}-message`;
  const toneColor = message ? TONE_COLOR[tone] : undefined;
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="flex flex-col gap-2 w-full">
      <label htmlFor={inputId} className="font-poppins text-[14px] font-normal text-[#616975] leading-none">
        {label}
      </label>
      <div
        onClick={() => inputRef.current?.focus()}
        className={`relative flex items-center h-[60px] px-4 bg-white border border-solid rounded-[12px] box-border cursor-text font-poppins text-[16px] text-[#08080a] transition-colors duration-200 ${toneColor ? "" : "border-[#DEE3ED] focus-within:border-[#99A3B2]"}`}
        style={toneColor ? { borderColor: toneColor } : undefined}
      >
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          inputMode="decimal"
          pattern={`[0-9]*[.,]?[0-9]{0,${maxDecimals}}`}
          autoComplete="off"
          value={value}
          placeholder={unit}
          disabled={disabled}
          aria-invalid={tone === "error" && !!message ? true : undefined}
          aria-describedby={message ? messageId : undefined}
          onChange={(e) => onChange(sanitizeDecimal(e.target.value, value, maxDecimals))}
          className="w-full min-w-0 p-0 m-0 border-0 bg-transparent outline-none font-poppins text-[16px] text-[#08080a] placeholder:text-[#99A3B2]"
        />
        {/* Invisible copy of the value pushes the unit to right after the number ("179,5 cm"). */}
        {value && (
          <span aria-hidden className="pointer-events-none absolute inset-y-0 left-4 flex items-center whitespace-pre font-poppins text-[16px]">
            <span className="invisible">{value}</span>
            <span className="ml-1 text-[#616975]">{unit}</span>
          </span>
        )}
      </div>
      {message && (
        <p id={messageId} className="m-0 font-poppins text-[13px] font-medium" style={{ color: toneColor }}>
          {message}
        </p>
      )}
    </div>
  );
};

export default DecimalInput;
